/**
 * Master list of standardised document types.
 * Kept in sync with the backend AI extraction engine (n8n / Gemini).
 * ALL front-end components must import from here — never hard-code type strings.
 */

export const MASTER_DOC_TYPES = [
  "Factura Internacional",
  "Factura de Compras",
  "Packing List",
  "BL",
  "Guía Aérea",
  "Declaración de Importación",
  "Declaración Andina de Valor",
  "Certificado de Origen",
  "Póliza de Seguro",
  "Certificado Técnico",
  "Orden de Compra",
  "Documento de Puerto",
  "Otro",
] as const;

export type MasterDocType = (typeof MASTER_DOC_TYPES)[number];

/** Normalise a tipo_documento string for case-insensitive comparison. */
export const normaliseTipo = (t?: string | null) => (t ?? "").toLowerCase().trim();

/** Helpers used by chameleon tables and conditional rendering. */
export const isFinancialType = (tipo?: string | null) => {
  const n = normaliseTipo(tipo);
  return n === "factura internacional" || n === "factura de compras";
};

export const isPackingType = (tipo?: string | null) =>
  normaliseTipo(tipo) === "packing list";

export const isTransportType = (tipo?: string | null) => {
  const n = normaliseTipo(tipo);
  return n === "bl" || n === "guía aérea" || n === "guia aerea" || n === "documento de puerto";
};

export const isNonFinancialType = (tipo?: string | null) =>
  isPackingType(tipo) || isTransportType(tipo);

/**
 * Documentos cuya naturaleza es generar gastos prorrateables (filas en `gastos_embarque`).
 * Por diseño del motor: BL, Guía Aérea, Documento de Puerto, Póliza de Seguro y
 * Factura de Compras (agencias, fletes locales, etc.) inyectan en `gastos_embarque`.
 * La Declaración de Importación queda excluida — actualiza impuestos en cabecera.
 */
export const isExpenseType = (tipo?: string | null) => {
  const n = normaliseTipo(tipo);
  return (
    n === "bl" ||
    n === "guía aérea" ||
    n === "guia aerea" ||
    n === "documento de puerto" ||
    n === "póliza de seguro" ||
    n === "poliza de seguro" ||
    n === "factura de compras"
  );
};

/**
 * Documentos que SÍ generan ítems de inventario auditables en `base_inventario`.
 * Solo Factura Internacional y Packing List producen líneas de inventario.
 */
export const hasInventoryItems = (tipo?: string | null) => {
  const n = normaliseTipo(tipo);
  return n === "factura internacional" || n === "packing list";
};
